import { ActualProps, AnyActualProp } from '~/utils/editor/versioning/migrators'
import { ActualVersion as BackgroundProps } from '~/utils/editor/versioning/background'
import { ObjectType } from '~/static/enums/object-type'
import { EditorData } from '~/static/enums/editor-state'
import { EditorObject, ObjectState } from '~/utils/editor/object.class'
import { ActualVersion as ImageProps } from '~/utils/editor/versioning/image'

export interface ObjectData<T extends ObjectType> {
  type: T
  index: number
  data: ActualProps[T]
  states: string[]
}

export interface BufferedObject {
  sort: number
  object: AnyActualProp
}

export interface FabricCanvasInterface {
  addObjectsFromJson: (objects: { object: AnyActualProp, sort: number }[]) => Promise<void>
  addImage: (url: string, fileName?: string, data?: ImageProps, sort?: number) => Promise<EditorObject>
  addText: (text?: string) => Promise<EditorObject>
  addIcon: (icon: string) => Promise<EditorObject>
  addSvgFigure: (figureIndex: number) => Promise<EditorObject>
  addLine: (lineIndex: number) => Promise<EditorObject>
  deleteObject: (ids?: string[]) => Promise<void>
  applyBackgroundModify: (data: BackgroundProps) => Promise<void>
  changeObjectSort: (from: number, to: number) => Promise<void>
  applyModification: (id: string, data: AnyActualProp) => Promise<void>
  refreshPreviews: (ids: string[]) => Promise<void>
  moveObject: (ids: string[], direction: 'vertical' | 'horizontal', offset: number) => Promise<void>
  select: (ids: string[]) => Promise<void>
  unselect: () => Promise<void>
  addObjectState: (key: string, state: ObjectState) => Promise<void>
  removeObjectState: (key: string) => Promise<void>
  setEditorData: (data: EditorData) => Promise<void>
  getObjects: () => AnyActualProp[]
  getBackground: () => BackgroundProps
  blockRender: () => void
  unblockRender: () => void
}

export enum Action {
  Drag = 'drag',
  ScaleX = 'scaleX',
  ScaleY = 'scaleY',
  Scale = 'scale',
  Rotate = 'rotate',
  Sort = 'sort',
  MoveArrowUp = 'move-arrow-up',
  MoveArrowDown = 'move-arrow-down',
  MoveArrowLeft = 'move-arrow-left',
  MoveArrowRight = 'move-arrow-right',
  Erase = 'erase',
  Flip = 'flip',
  Hide = 'hide',
  Lock = 'lock',
  ChangeCanvasFill = 'CanvasFill',
  ChangeCanvasFillColor = 'CanvasFillColor',
  ChangeCanvasStroke = 'CanvasStroke',
  ChangeCanvasSize = 'CanvasSize',
  RoundText = 'RoundText',
  ChangeFont = 'ChangeFont',
  ChangeFontFamily = 'ChangeFontFamily',
  ChangeFontVariant = 'ChangeFontVariant',
  ChangeColor = 'ChangeColor',
  ChangeShadow = 'ChangeShadow',
  ChangeShadowColor = 'ChangeShadowColor',
  ChangeStroke = 'ChangeStroke',
  ChangeStrokeColor = 'ChangeStrokeColor',
  ChangeSize = 'ChangeSize',
  Crop = 'Crop',
  ReplaceImage = 'ReplaceImage',
  ChangeTransparency = 'ChangeTransparency',
  Correction = 'Correction',
  CorrectBrightness = 'CorrectBrightness',
  CorrectContrast = 'CorrectContrast',
  CorrectSaturation = 'CorrectSaturation',
  CorrectColorfulness = 'CorrectColorfulness',
  CorrectHue = 'CorrectHue',
  AddObject = 'AddObject',
  PasteObject = 'PasteObject',
  DeleteObject = 'DeleteObject',
  EditText = 'EditText',
  CustomRotate = 'CustomRotate',
}

export const ActionsGroups = {
  CanvasThrowed: [
    Action.Drag,
    Action.ScaleX,
    Action.ScaleY,
    Action.Scale,
    Action.Rotate,
  ] as const,
  EditorModifications: [
    Action.Erase,
    Action.Flip,
    Action.Hide,
    Action.Lock,
    Action.RoundText,
    Action.ChangeFont,
    Action.ChangeFontFamily,
    Action.ChangeFontVariant,
    Action.ChangeColor,
    Action.ChangeShadow,
    Action.ChangeShadowColor,
    Action.ChangeStroke,
    Action.ChangeStrokeColor,
    Action.ChangeSize,
    Action.Crop,
    Action.ReplaceImage,
    Action.ChangeTransparency,
    Action.Correction,
    Action.CorrectBrightness,
    Action.CorrectContrast,
    Action.CorrectSaturation,
    Action.CorrectColorfulness,
    Action.CorrectHue,
    Action.EditText,
    Action.CustomRotate,
  ] as const,
  Sorting: [ Action.Sort ] as const,
  Background: [
    Action.ChangeCanvasSize,
    Action.ChangeCanvasFill,
    Action.ChangeCanvasFillColor,
    Action.ChangeCanvasStroke,
  ] as const,
  MoveByArrows: [
    Action.MoveArrowUp,
    Action.MoveArrowDown,
    Action.MoveArrowLeft,
    Action.MoveArrowRight,
  ] as const,
  Creating: [
    Action.AddObject,
  ] as const,
  Pasing: [
    Action.PasteObject,
  ] as const,
  Deleting: [
    Action.DeleteObject,
  ] as const,
}

export type ManageObject = {
  object: string
  sort: number
}

export type ObjectModification = {
  id: string
  oldProps: AnyActualProp
  newProps: AnyActualProp
}

export type BackgroundModification = {
  oldProps: BackgroundProps
  newProps: BackgroundProps
}

export type ObjectSorting = {
  from: number
  to: number
}
